import { Button } from "@shared/ui/button";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";

type Props = {
	children: ReactNode;
	fallback?: ReactNode;
};

type State = {
	hasError: boolean;
	error: Error | null;
};

function ErrorFallback({
	error,
	onReset,
}: {
	error: Error | null;
	onReset: () => void;
}) {
	return (
		<div className="flex   w-full flex-col items-center justify-center gap-6 bg-background text-foreground">
			<div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10 ring-1 ring-destructive/30">
				<span className="text-3xl">⚠️</span>
			</div>
			<div className="text-center space-y-2">
				<h1 className="text-2xl font-semibold">
					<FormattedMessage id="error.boundary.title" defaultMessage="Something went wrong" />
				</h1>
				<p className="text-sm text-muted-foreground max-w-sm">
					<FormattedMessage
						id="error.boundary.description"
						defaultMessage="An unexpected error occurred. Try refreshing the page or contact support if the problem persists."
					/>
				</p>
				{error && (
					<p className="text-xs font-mono text-destructive bg-destructive/10 px-3 py-2 rounded-md mt-2">
						{error.message}
					</p>
				)}
			</div>
			<div className="flex gap-2">
				<Button variant="outline" onClick={() => window.location.reload()}>
					<FormattedMessage id="error.boundary.refresh" defaultMessage="Refresh page" />
				</Button>
				<Button onClick={onReset}>
					<FormattedMessage id="error.boundary.retry" defaultMessage="Try again" />
				</Button>
			</div>
		</div>
	);
}

export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		console.error("ErrorBoundary caught:", error, info);
	}

	reset = () => {
		this.setState({ hasError: false, error: null });
	};

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) return this.props.fallback;

			return <ErrorFallback error={this.state.error} onReset={this.reset} />;
		}

		return this.props.children;
	}
}
